import * as React from "react";
import { Bell, Grip, Settings } from "lucide-react";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import AppTile from "@/components/apps/app-tile";
import { IconoirRender } from "@/components/icons/iconoir-map";
import { getAeroTrakAppDtos } from "@/hooks/services/aerotrak/handlers/aerotrak-apps-api";
import { cn } from "@/lib/utils";
import { logout } from "@/hooks/services/workchestra/handler/auth";
import { toastManager } from "@/lib/toast-manager";
import {
  NotificationPanel,
  Notification,
  getCategoryRelatedObjectNames,
  mapNotificationDto,
  libraryRelatedObjectNames,
} from "@/components/notifications";
import {
  fetchNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
} from "@/hooks/services/workchestra/handler/notifications-api";
import { useNotificationStore } from "@/stores/notification-store";
import { useGetData } from "@/hooks/services/use-query-hooks";
import { GetAppConfigHandler } from "@/hooks/services/workchestra/handler/config-api";
import { UserMenu } from "@/components/layout/user-menu";

export function Header() {
  const [isUserMenuOpen, setIsUserMenuOpen] = React.useState(false);
  const [isAppsOpen, setIsAppsOpen] = React.useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = React.useState(false);
  const [apps, setApps] = React.useState<
    Array<{
      id: number;
      name: string;
      description?: string;
      iconLibrary?: string;
      iconName?: string;
      iconColor?: string | null;
      url: string;
      status?: string;
    }>
  >([]);
  const [appsLoading, setAppsLoading] = React.useState(false);
  const [appsError, setAppsError] = React.useState<string | null>(null);

  // Notification state
  const [notifications, setNotifications] = React.useState<Notification[]>([]);
  const { unreadCount, decrementUnreadCount, resetUnreadCount, initUnreadCount } = useNotificationStore();

  // Fetch total unread count on mount (deduplicated via store)
  React.useEffect(() => {
    initUnreadCount();
  }, [initUnreadCount]);

  // Fetch app config for redirect URL
  const { data: appConfig } = useGetData(GetAppConfigHandler());

  const aerotrakRedirectUrl = appConfig?.appUrls?.aerotrak || "/";

  const sortNotificationsByNewest = React.useCallback(
    (items: Notification[]) =>
      [...items].sort(
        (a, b) => b.createdAt.getTime() - a.createdAt.getTime(),
      ),
    [],
  );

  // Fetch notifications based on category
  const fetchNotificationsByCategory = React.useCallback(
    async (category: string) => {
      try {
        if (category.toLowerCase() === "all") {
          const [allRes, libraryRes] = await Promise.all([
            fetchNotifications({
              page: 1,
              itemsPerPage: 10,
              relatedObjectName: undefined,
            }),
            fetchNotifications({
              page: 1,
              itemsPerPage: 10,
              relatedObjectName: libraryRelatedObjectNames,
            }),
          ]);

          const merged = [...allRes.items];
          const existingIds = new Set(merged.map((item) => item.id));

          libraryRes.items.forEach((item) => {
            if (!existingIds.has(item.id)) {
              merged.push(item);
            }
          });

          setNotifications(
            sortNotificationsByNewest(merged.map(mapNotificationDto)),
          );
          return;
        }

        const relatedObjectNames = getCategoryRelatedObjectNames(category);
        const notifRes = await fetchNotifications({
          page: 1,
          itemsPerPage: 10,
          relatedObjectName: relatedObjectNames,
        });
        setNotifications(
          sortNotificationsByNewest(notifRes.items.map(mapNotificationDto)),
        );
      } catch (err: any) {
        toastManager.showError(err?.message || "Failed to load notifications");
      }
    },
    [libraryRelatedObjectNames, sortNotificationsByNewest],
  );

  // Handle category change
  const handleCategoryChange = React.useCallback(
    (category: string) => {
      fetchNotificationsByCategory(category);
    },
    [fetchNotificationsByCategory],
  );

  React.useEffect(() => {
    if (!isNotificationsOpen) return;

    let cancelled = false;

    const fetchInitialData = async () => {
      try {
        const [appsRes] = await Promise.all([
          getAeroTrakAppDtos(),
          fetchNotificationsByCategory("all"),
        ]);

        if (!cancelled) {
          setApps(appsRes.filter((a) => a.isAssigned !== false));
        }
      } catch (err: any) {
        if (cancelled) return;
        toastManager.showError(
          err?.message || "Failed to load notification data",
        );
      }
    };

    fetchInitialData();

    return () => {
      cancelled = true;
    };
  }, [fetchNotificationsByCategory, isNotificationsOpen]);

  // Fetch assigned apps each time the apps popover is opened
  React.useEffect(() => {
    if (!isAppsOpen) return;
    let cancelled = false;
    setAppsLoading(true);
    getAeroTrakAppDtos()
      .then((res) => {
        if (cancelled) return;
        setApps(res.filter((a) => a.isAssigned !== false));
        setAppsError(null);
      })
      .catch((err: any) => {
        if (cancelled) return;
        const msg = err?.message || "Failed to load apps";
        setAppsError(msg);
      })
      .finally(() => {
        if (!cancelled) setAppsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAppsOpen]);

  const handleLogout = async () => {
    logout();
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      await markNotificationAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)),
      );
      decrementUnreadCount();
    } catch (error: any) {
      toastManager.showError(
        error?.message || "Failed to mark notification as read",
      );
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      resetUnreadCount();
    } catch (error: any) {
      toastManager.showError(
        error?.message || "Failed to mark all notifications as read",
      );
    }
  };

  const handleViewAll = () => {
    setIsNotificationsOpen(false);
    const rawWorkchestraUrl = import.meta.env.VITE_WORKCHESTRA_BASE_URL || "";
    const workchestraBaseUrl = rawWorkchestraUrl.endsWith("/")
      ? rawWorkchestraUrl.slice(0, -1)
      : rawWorkchestraUrl;
    window.location.href = `${workchestraBaseUrl}/workchestra/notifications`;
  };

  return (
    <header className="aero:sticky aero:top-0 aero:z-50 aero:flex aero:h-16 aero:w-full aero:shrink-0 aero:items-center aero:border-b aero:bg-white aero:px-5 aero:py-2.5 aero:md:px-10 aero:md:py-2.5 aero:border-neutral-300 aero:overscroll-none">
      <div className="aero:mx-auto aero:w-full aero:flex aero:items-center aero:justify-between">
        <div className="aero:flex aero:items-center aero:gap-3">
          <a
            href={aerotrakRedirectUrl}
            rel="noopener noreferrer"
            className="aero:flex aero:flex-col aero:items-start aero:gap-1"
          >
            <img
              src="/images/aerotrak-logo.svg"
              alt="AeroTrak"
              className="aero:h-11 aero:w-24"
            />
            <span className="aero:sr-only">Home</span>
          </a>
        </div>

        <div className="aero:flex aero:items-center aero:gap-3">
          <div className="aero:hidden aero:md:flex aero:items-center aero:gap-3">
            <Popover open={isAppsOpen} onOpenChange={setIsAppsOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-haspopup="menu"
                  aria-expanded={isAppsOpen}
                  className="aero:h-9 aero:w-9 aero:flex aero:items-center aero:justify-center aero:rounded aero:cursor-pointer aero:transition-colors aero:hover:bg-neutral-100 aero:active:bg-neutral-200"
                >
                  <Grip className="aero:h-6 aero:w-6" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                side="bottom"
                align="end"
                collisionPadding={{ left: 32, right: 128, top: 12, bottom: 12 }}
                className="aero:w-[480px] aero:max-w-[90vw] aero:p-4 aero:rounded-xl aero:border aero:border-neutral-300 aero:bg-white aero:shadow-sm"
              >
                {appsLoading && (
                  <div className="aero:text-xs aero:text-neutral-500">
                    Loading...
                  </div>
                )}
                {!appsLoading && appsError && (
                  <div className="aero:text-xs aero:text-red-600">
                    {appsError}
                  </div>
                )}
                {!appsLoading && !appsError && apps.length === 0 && (
                  <div className="aero:text-xs aero:text-neutral-500">
                    No apps assigned.
                  </div>
                )}
                {!appsLoading && !appsError && apps.length > 0 && (
                  <div
                    role="list"
                    className={cn(
                      "aero:grid aero:grid-cols-2 aero:sm:grid-cols-3 aero:gap-4",
                    )}
                  >
                    {apps.map((app) => (
                      <div key={app.id} role="listitem">
                        <AppTile
                          id={app.id}
                          name={app.name}
                          href={app.url}
                          icon={
                            app.iconLibrary === "iconoir" && app.iconName ? (
                              <IconoirRender
                                name={app.iconName}
                                color={app.iconColor || undefined}
                                size={32}
                                strokeWidth={1.6}
                              />
                            ) : (
                              <span className="aero:text-[10px] aero:text-neutral-400">
                                NA
                              </span>
                            )
                          }
                          newTab={false}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </PopoverContent>
            </Popover>
            <Popover
              open={isNotificationsOpen}
              onOpenChange={setIsNotificationsOpen}
            >
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-haspopup="menu"
                  aria-expanded={isNotificationsOpen}
                  className="aero:relative aero:h-9 aero:w-9 aero:flex aero:items-center aero:justify-center aero:rounded aero:cursor-pointer aero:transition-colors aero:hover:bg-neutral-100 aero:active:bg-neutral-200"
                >
                  <Bell className="aero:h-6 aero:w-6" />
                  {unreadCount > 0 && (
                    <span className="aero:absolute aero:-right-0.5 aero:-top-0.5 aero:flex aero:h-4 aero:min-w-4 aero:items-center aero:justify-center aero:!rounded-full aero:bg-red-600 aero:text-[10px] aero:font-bold aero:text-white">
                      {unreadCount}
                    </span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent
                side="bottom"
                align="end"
                collisionPadding={{ left: 32, right: 32, top: 12, bottom: 12 }}
                className="aero:w-[520px] aero:max-w-[90vw] aero:p-0 aero:border-0 aero:bg-transparent aero:shadow-none"
              >
                <NotificationPanel
                  notifications={notifications}
                  apps={apps}
                  onMarkAsRead={handleMarkAsRead}
                  onMarkAllAsRead={handleMarkAllAsRead}
                  onViewAll={handleViewAll}
                  onCategoryChange={handleCategoryChange}
                />
              </PopoverContent>
            </Popover>
            <div className="aero:h-9 aero:w-9 aero:flex aero:items-center aero:justify-center aero:rounded aero:cursor-pointer aero:transition-colors aero:hover:bg-neutral-100 aero:active:bg-neutral-200">
              <Settings className="aero:h-6 aero:w-6" />
            </div>
            <Popover open={isUserMenuOpen} onOpenChange={setIsUserMenuOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-haspopup="menu"
                  aria-expanded={isUserMenuOpen}
                  className="aero:h-9 aero:w-9 aero:flex aero:items-center aero:justify-center aero:rounded-full aero:cursor-pointer aero:transition-colors aero:hover:bg-neutral-100 aero:active:bg-neutral-200 aero:group"
                >
                  <Avatar className="aero:size-6 aero:transition-colors aero:ring-1 aero:ring-transparent aero:group-hover:ring-neutral-300">
                    <AvatarFallback>
                      <svg
                        viewBox="0 0 24 24"
                        className="aero:h-6 aero:w-6 aero:text-muted-foreground"
                      >
                        <path
                          fill="currentColor"
                          d="M12 12c2.761 0 5-2.239 5-5s-2.239-5-5-5-5 2.239-5 5 2.239 5 5 5m0 2c-3.866 0-7 3.134-7 7h2a5 5 0 0 1 10 0h2c0-3.866-3.134-7-7-7"
                        />
                      </svg>
                    </AvatarFallback>
                  </Avatar>
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                className="aero:w-[200px] aero:p-0 aero:overflow-hidden aero:rounded-lg aero:border aero:border-neutral-300 aero:shadow-md"
              >
                <UserMenu
                  onLogout={handleLogout}
                  onItemSelect={() => setIsUserMenuOpen(false)}
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>
    </header>
  );
}
